/**
 * Organization Service
 * Handles all organization-related API calls
 */

import apiClient from './api';
import type { ApiResponse, Organization, UpdateOrganizationRequest } from '../types';

export const organizationService = {
  /**
   * Get current organization details
   */
  getOrganization: async (): Promise<Organization> => {
    const response = await apiClient.get<ApiResponse<{ organization: Organization }>>(
      '/organization'
    );
    if (response.data.success && response.data.data) {
      return response.data.data.organization;
    }
    throw new Error(response.data.message || 'Failed to fetch organization');
  },

  /**
   * Update organization details
   */
  updateOrganization: async (data: UpdateOrganizationRequest): Promise<Organization> => {
    const response = await apiClient.patch<ApiResponse<{ organization: Organization }>>(
      '/organization',
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data.organization;
    }
    throw new Error(response.data.message || 'Failed to update organization');
  },

  /**
   * Upload organization logo
   */
  uploadLogo: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await apiClient.post<ApiResponse<{ logoUrl: string }>>(
      '/organization/logo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.success && response.data.data) {
      return response.data.data.logoUrl;
    }
    throw new Error(response.data.message || 'Failed to upload logo');
  },

  /**
   * Update cycle settings
   */
  updateCycleSettings: async (data: {
    cycleType?: string;
    autoCreateCycles?: boolean;
    advanceCreationDays?: number;
    reminderEnabled?: boolean;
    reminderDays?: number;
    requireOKRApproval?: boolean;
    fiscalYearStartMonth?: number;
  }): Promise<void> => {
    const response = await apiClient.patch<ApiResponse>(
      '/organization/cycle-settings',
      data
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update cycle settings');
    }
  },

  /**
   * Update organization settings (will be merged with existing settings)
   * @param settings - Settings key-value pairs to update
   */
  updateSettings: async (settings: Record<string, unknown>): Promise<Organization> => {
    const response = await apiClient.patch<ApiResponse<{ organization: Organization }>>(
      '/organization',
      { settings }
    );
    if (response.data.success && response.data.data) {
      return response.data.data.organization;
    }
    throw new Error(response.data.message || 'Failed to update organization settings');
  },
};
